const STORAGE_KEY = 'midi.settings';
const DEFAULTS = {
  enabled: false,
  inputId: 'all',
  outputId: '',
  clockMode: 'off', // off | send | receive
  receiveTransport: true,
  triggerNote: null,
};

const loadSettings = () => {
  try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') }; } catch { return { ...DEFAULTS }; }
};

export const midiNoteName = (n) => `${['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'][n % 12]}${Math.floor(n / 12) - 1}`;

class MidiManager {
  constructor() {
    this.supported = typeof navigator !== 'undefined' && !!navigator.requestMIDIAccess;
    this.settings = loadSettings();
    this.access = null;
    this.error = null;
    this.learning = false;
    this.externalBpm = null;
    this.listeners = new Set();
    this.noteListeners = new Set();
    this.transport = null;
    this.clockTimer = null;
    this.clockIntervals = [];
    this.lastClock = 0;
    this.lastBpmPush = 0;
    if (this.settings.enabled) this.enable();
  }

  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  onNote(fn) { this.noteListeners.add(fn); return () => this.noteListeners.delete(fn); }
  emit() { this.listeners.forEach((fn) => fn()); }

  get inputs() { return this.access ? [...this.access.inputs.values()] : []; }
  get outputs() { return this.access ? [...this.access.outputs.values()] : []; }

  update(patch) {
    this.settings = { ...this.settings, ...patch };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    if ('clockMode' in patch || 'outputId' in patch) this.restartClock();
    if ('clockMode' in patch) { this.externalBpm = null; this.clockIntervals = []; }
    this.emit();
  }

  async enable() {
    if (!this.supported) return;
    try {
      this.access = await navigator.requestMIDIAccess({ sysex: false });
      this.error = null;
      this.access.onstatechange = () => { this.bindInputs(); this.emit(); };
      this.bindInputs();
      this.update({ enabled: true });
    } catch (e) {
      this.error = e?.name === 'SecurityError' || e?.name === 'NotAllowedError' ? 'denied' : 'error';
      this.update({ enabled: false });
    }
  }

  disable() {
    this.inputs.forEach((i) => { i.onmidimessage = null; });
    this.stopClock();
    this.access = null;
    this.update({ enabled: false });
  }

  bindInputs() {
    this.inputs.forEach((input) => {
      input.onmidimessage = (e) => {
        const { inputId } = this.settings;
        if (inputId !== 'all' && inputId !== input.id) return;
        this.handleMessage(e.data, e.timeStamp);
      };
    });
  }

  setLearning(on) { this.learning = on; this.emit(); }

  handleMessage(data, ts) {
    const [status, d1, d2] = data;
    const t = this.transport;
    if ((status & 0xf0) === 0x90 && d2 > 0) {
      if (this.learning) { this.learning = false; this.update({ triggerNote: d1 }); return; }
      if (d1 === this.settings.triggerNote) t?.toggle();
      else this.noteListeners.forEach((fn) => fn(d1));
      return;
    }
    if (!this.settings.receiveTransport && (status === 0xfa || status === 0xfb || status === 0xfc)) return;
    if (status === 0xfa || status === 0xfb) { this.clockIntervals = []; if (t && !t.isPlaying()) t.toggle(); return; }
    if (status === 0xfc) { if (t?.isPlaying()) t.toggle(); return; }
    if (status === 0xf8 && this.settings.clockMode === 'receive') this.handleClock(ts);
  }

  handleClock(ts) {
    if (this.lastClock && ts - this.lastClock < 500) {
      this.clockIntervals.push(ts - this.lastClock);
      if (this.clockIntervals.length > 48) this.clockIntervals.shift();
    } else {
      this.clockIntervals = [];
    }
    this.lastClock = ts;
    if (this.clockIntervals.length < 24 || ts - this.lastBpmPush < 300) return;
    this.lastBpmPush = ts;
    const avg = this.clockIntervals.reduce((a, b) => a + b, 0) / this.clockIntervals.length;
    const bpm = Math.round(60000 / (avg * 24));
    if (bpm !== this.externalBpm) { this.externalBpm = bpm; this.emit(); }
    if (this.transport && Math.abs(this.transport.getBpm() - bpm) >= 1) this.transport.setBpm(bpm);
  }

  output() {
    if (this.settings.clockMode !== 'send' || !this.settings.outputId) return null;
    return this.access?.outputs.get(this.settings.outputId) || null;
  }

  // Called by pages when their transport starts/stops.
  notifyPlay(playing) {
    this.playing = playing;
    const out = this.output();
    if (!out) return;
    if (playing) { out.send([0xfa]); this.startClock(); } else { this.stopClock(); out.send([0xfc]); }
  }

  startClock() {
    this.stopClock();
    const out = this.output();
    if (!out) return;
    let next = performance.now();
    const pump = () => {
      const bpm = this.transport?.getBpm() || 120;
      const step = 60000 / (bpm * 24);
      const horizon = performance.now() + 150;
      if (next < performance.now() - 1000) next = performance.now();
      while (next < horizon) {
        out.send([0xf8], next);
        next += step;
      }
    };
    pump();
    this.clockTimer = setInterval(pump, 20);
  }

  stopClock() { clearInterval(this.clockTimer); this.clockTimer = null; }

  restartClock() { if (this.playing && this.output()) this.startClock(); else this.stopClock(); }
}

export const midi = new MidiManager();
